import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

type Tab = 'file' | 'text';

export function WikiUpload() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('file');
  const [categorySlug, setCategorySlug] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // 파일 업로드 상태.
  const [file, setFile] = useState<File | null>(null);

  // 텍스트 입력 상태.
  const [title, setTitle] = useState('');
  const [rawText, setRawText] = useState('');
  const [textMode, setTextMode] = useState<'auto-cleanup' | 'as-is'>('auto-cleanup');

  const categoriesQuery = useQuery({
    queryKey: ['wiki', 'categories'],
    queryFn: api.wikiCategories,
  });
  const categories = categoriesQuery.data?.categories ?? [];

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('파일을 선택해 주세요.');
      return api.wikiUpload(file, categorySlug || null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
      navigate({ to: '/wikis/documents' });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const textMutation = useMutation({
    mutationFn: async () => {
      if (title.trim().length === 0) throw new Error('제목을 입력해 주세요.');
      if (rawText.trim().length === 0) throw new Error('본문을 입력해 주세요.');
      return api.wikiUploadFromText({
        title: title.trim(),
        categorySlug: categorySlug || null,
        rawText,
        mode: textMode,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
      navigate({ to: '/wikis/documents' });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  function onSubmitFile(e: FormEvent) {
    e.preventDefault();
    setError(null);
    uploadMutation.mutate();
  }

  function onSubmitText(e: FormEvent) {
    e.preventDefault();
    setError(null);
    textMutation.mutate();
  }

  const charCount = rawText.length;
  const charLimit = 200_000;

  return (
    <div className="mx-auto max-w-2xl px-6 py-10">
      <header className="mb-6">
        <Link
          to="/wikis"
          className="mb-2 inline-block text-xs text-foreground-muted hover:text-accent"
        >
          ← 위키 목차
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">자료 올리기</h1>
        <p className="mt-1 text-sm text-foreground-muted">
          파일을 올리거나 사내 게시판/문서에서 복사한 텍스트를 붙여넣으면 LLM 이 마크다운 위키
          페이지로 정리합니다.
        </p>
      </header>

      <div className="mb-4 inline-flex items-center gap-1 rounded-md border border-border bg-surface p-0.5">
        <TabButton active={tab === 'file'} onClick={() => setTab('file')}>
          파일 업로드
        </TabButton>
        <TabButton active={tab === 'text'} onClick={() => setTab('text')}>
          텍스트 직접 입력
        </TabButton>
      </div>

      {/* 카테고리 — 두 탭 공용 */}
      <div className="mb-4">
        <label className="mb-1 block text-sm font-medium" htmlFor="wiki-upload-category">
          카테고리
        </label>
        <select
          id="wiki-upload-category"
          value={categorySlug}
          onChange={(e) => setCategorySlug(e.target.value)}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
        >
          <option value="">선택 안 함 (관리자만 업로드 가능)</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name} ({c.slug}){c.uploadOpenToUsers ? ' — 누구나 업로드' : ' — 관리자만'}
            </option>
          ))}
        </select>
      </div>

      {tab === 'file' ? (
        <form className="space-y-4" onSubmit={onSubmitFile}>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="wiki-upload-file">
              파일
            </label>
            <input
              id="wiki-upload-file"
              type="file"
              accept=".pdf,.hwp,.docx,.pptx,.xlsx,.md,.txt"
              onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
              className="block w-full text-sm text-foreground-muted file:mr-4 file:rounded-md file:border-0 file:bg-accent file:px-4 file:py-2 file:text-sm file:font-medium file:text-accent-foreground"
            />
            <p className="mt-1 text-xs text-foreground-faint">
              지원 확장자: pdf, hwp, docx, pptx, xlsx, md, txt — 최대 100 MB
            </p>
          </div>

          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!file || uploadMutation.isPending}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploadMutation.isPending ? '업로드 중…' : '업로드'}
          </button>
        </form>
      ) : (
        <form className="space-y-4" onSubmit={onSubmitText}>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="wiki-text-title">
              제목
            </label>
            <input
              id="wiki-text-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 2026 상반기 보안 교육 안내"
              maxLength={200}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="wiki-text-body">
              본문 (사내 게시판/메일/문서에서 복사한 텍스트)
            </label>
            <textarea
              id="wiki-text-body"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              rows={16}
              maxLength={charLimit}
              placeholder="여기에 본문을 붙여넣으세요. 줄바꿈/들여쓰기는 그대로 유지됩니다."
              className="w-full resize-y rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs leading-relaxed focus:border-accent focus:outline-none"
            />
            <p className="mt-1 text-right text-xs text-foreground-faint">
              {charCount.toLocaleString()} / {charLimit.toLocaleString()} 자
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">정리 방식</label>
            <div className="flex flex-col gap-2 text-sm">
              <label className="inline-flex items-start gap-2">
                <input
                  type="radio"
                  name="text-mode"
                  value="auto-cleanup"
                  checked={textMode === 'auto-cleanup'}
                  onChange={() => setTextMode('auto-cleanup')}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">LLM 으로 markdown 정리</span>
                  <span className="ml-1 text-xs text-foreground-muted">
                    (헤딩/리스트/표를 자동 적용. 원문 의미는 보존)
                  </span>
                </span>
              </label>
              <label className="inline-flex items-start gap-2">
                <input
                  type="radio"
                  name="text-mode"
                  value="as-is"
                  checked={textMode === 'as-is'}
                  onChange={() => setTextMode('as-is')}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">정리 없이 그대로 등록</span>
                  <span className="ml-1 text-xs text-foreground-muted">
                    (이미 markdown 으로 작성된 텍스트인 경우)
                  </span>
                </span>
              </label>
            </div>
          </div>

          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={
              title.trim().length === 0 || rawText.trim().length === 0 || textMutation.isPending
            }
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {textMutation.isPending
              ? textMode === 'auto-cleanup'
                ? '정리 중…'
                : '등록 중…'
              : textMode === 'auto-cleanup'
                ? '정리하고 등록'
                : '그대로 등록'}
          </button>
        </form>
      )}

      <div className="mt-8 rounded-md border border-border bg-surface p-4 text-sm text-foreground-muted">
        <h2 className="mb-2 font-medium text-foreground">처리 흐름</h2>
        <ol className="ml-5 list-decimal space-y-1">
          {tab === 'file' ? (
            <>
              <li>파일이 저장소에 업로드됩니다.</li>
              <li>
                파일 형식에 맞는 변환기 (PDF→Java jar, HWP→Python pyhwp 등) 가 마크다운으로
                변환합니다.
              </li>
            </>
          ) : (
            <>
              <li>입력한 텍스트가 LLM 으로 markdown 정리됩니다 (정리 모드 선택 시).</li>
              <li>정리된 마크다운이 .md 파일로 저장소에 보관됩니다.</li>
            </>
          )}
          <li>LLM 이 위키 인덱스를 확인하고 신규/갱신할 페이지를 결정해 자동 작성합니다.</li>
          <li>
            완료되면{' '}
            <Link to="/wikis/documents" className="text-accent underline">
              내 자료
            </Link>{' '}
            페이지에서 결과를 확인할 수 있습니다.
          </li>
        </ol>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
        active
          ? 'bg-accent text-accent-foreground'
          : 'text-foreground-muted hover:bg-surface-muted/60 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
